import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import styled from "styled-components";

import { RootState } from "../../../../../../redux/store";
import { ConfirmationInitializationMessage } from "@components/Common/ConfirmationInitializationMessage";
import { ResetIcon } from "@components/Common/SvgIcons/ResetIcon";
import type { ResettableSection } from "../../../../../../redux/scanToSlice";
import { ScanToContext } from "..";
import { SCAN_TO_DEFAULT_MAIN_COLOR, ICON_SIZE_SMALL } from "./constants";

const ResetButtonRow = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-bottom: 12px;
`;

// Borderless brand-color link (icon + label) matching the per-rubric
// "Réinitialiser tout" controls — no frame, subtle tinted hover.
const ResetButton = styled.button<{ mainColor: string }>`
    padding: 4px 8px;
    background: none;
    color: ${({ mainColor }) => mainColor};
    border: none;
    border-radius: 4px;
    font-size: 11px;
    cursor: pointer;
    transition: background-color 0.2s ease;
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 6px;

    &:hover {
        background-color: ${({ mainColor }) => `${mainColor}20`};
    }
`;

type RubricResetButtonProps = {
    // The slice section this rubric maps to (e.g. "banner", "offers").
    // Used for the data-testid slug and the store-reset fallback path.
    section: ResettableSection;
    // Reset strategy — the two paths coexist by design and are mutually
    // exclusive (see handleConfirm), so a rubric uses exactly one; there is
    // no double-reset:
    //   • onReset PROVIDED — offers, actions, advertising, myOrder,
    //     miscellaneousSettings, paymentOptions: the rubric resets its OWN
    //     local state in place. Preferred for rubrics with rich local state
    //     that must refresh instantly without a remount.
    //   • onReset OMITTED — activity, serverCallOptions: falls back to
    //     resetRubric(section) from ScanToContext, which wipes the slice
    //     section and bumps a remount key so the tab re-seeds from defaults.
    //     This path is live (not legacy) — those two tabs have no local reset.
    onReset?: () => void;
};

// Per-rubric "Réinitialiser" button: resets ONLY this rubric to its defaults,
// after a confirmation dialog. The reset itself runs via onReset (local) or,
// when omitted, resetRubric(section) from ScanToContext (store wipe + remount).
export function RubricResetButton({
    section,
    onReset,
}: RubricResetButtonProps): JSX.Element {
    const { t } = useTranslation();
    const { resetRubric } = React.useContext(ScanToContext);
    const handleConfirm = () => {
        if (onReset) {
            onReset();
        } else {
            resetRubric(section);
        }
    };
    const mainColor =
        useSelector((state: RootState) => state.global.infoUser.maincolor) ||
        SCAN_TO_DEFAULT_MAIN_COLOR;
    const [isConfirmOpen, setIsConfirmOpen] = React.useState(false);

    // camelCase section → kebab-case testid slug (keeps data-testid kebab).
    const slug = section.replace(/([A-Z])/g, "-$1").toLowerCase();

    return (
        <ResetButtonRow>
            <ResetButton
                type="button"
                mainColor={mainColor}
                data-testid={`scan-to-${slug}-reset-btn`}
                onClick={() => setIsConfirmOpen(true)}
            >
                <ResetIcon
                    style={{ width: ICON_SIZE_SMALL, height: ICON_SIZE_SMALL }}
                />
                {t("Reset all")}
            </ResetButton>

            {isConfirmOpen && (
                <ConfirmationInitializationMessage
                    isModalOpened={isConfirmOpen}
                    setIsModalOpened={setIsConfirmOpen}
                    onConfirm={handleConfirm}
                />
            )}
        </ResetButtonRow>
    );
}
